/**
 * Employment-related constants
 * TASK-258: Shared constants for employment status inference
 */

/**
 * Keywords that indicate self-employment in occupation/position descriptions
 * Used by both frontend (riskProfileService) and backend (client_kyc_extraction_routes)
 * to infer employment status when not explicitly provided
 */
export const SELF_EMPLOYMENT_KEYWORDS = [
  'founder',
  'co-founder',
  'owner',
  'self-employed',
  'freelance',
  'freelancer',
  'consultant',
  'entrepreneur',
  'partner',
  'managing partner',
  'sole proprietor',
  'independent',
  'contractor',
] as const;

/**
 * Check if an occupation string indicates self-employment
 * @param occupation - The occupation/position string to check
 * @returns true if the occupation indicates self-employment
 */
export const isSelfEmploymentOccupation = (occupation: string): boolean => {
  const lowerOccupation = occupation.toLowerCase();
  return SELF_EMPLOYMENT_KEYWORDS.some(keyword => lowerOccupation.includes(keyword));
};

/**
 * Employment status values used in forms
 */
export const EMPLOYMENT_STATUS = {
  EMPLOYED: 'employed',
  SELF_EMPLOYED: 'selfEmployed',
  RETIRED: 'retired',
  UNEMPLOYED: 'unemployed',
  STUDENT: 'student',
} as const;

export type EmploymentStatus = typeof EMPLOYMENT_STATUS[keyof typeof EMPLOYMENT_STATUS];

/**
 * Infer employment status from occupation and employer name
 * @param occupation - The occupation/position string
 * @param employerName - The employer name (if any)
 * @returns The inferred employment status or undefined if cannot determine
 */
export const inferEmploymentStatus = (
  occupation?: string,
  employerName?: string
): EmploymentStatus | undefined => {
  if (!occupation && !employerName) {
    return undefined;
  }

  if (occupation && isSelfEmploymentOccupation(occupation)) {
    return EMPLOYMENT_STATUS.SELF_EMPLOYED;
  }

  if (employerName) {
    return EMPLOYMENT_STATUS.EMPLOYED;
  }

  return undefined;
};
